import React from 'react';

import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';

import { styles } from '../styles.ts';

interface DynamicPinFormProps {
    onSubmit: (response: string) => Promise<void>;
    error?: string | null;
    isLoading?: boolean;
}

/**
 * A code from an authenticator app is six digits. Always six: the server verifies with speakeasy's
 * defaults and `hotp.verifyDelta` returns nothing at all when the length does not match, so five or
 * seven is not a near miss, it is a wrong PIN — and a wrong PIN costs one of three attempts.
 *
 * This form used to ask for eight or more characters of A–Z and 0–9, with a thirty-two character
 * base32 string as its placeholder. That is the *seed* the person creating the secret is shown, not
 * the code their recipient reads off their phone. Nobody could open one of these: the button never
 * enabled.
 */
const CODE_LENGTH = 6;

export const DynamicPinForm = ({ onSubmit, error, isLoading }: DynamicPinFormProps) => {
    const [value, setValue] = React.useState('');
    const [localError, setLocalError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (error) {
            setLocalError(null);
        }
    }, [error]);

    const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalError(null);
        // Digits only, and never more than the code is. Pasting "123 456" or a code with a space in
        // the middle — which is how every authenticator app shows it — still works.
        setValue(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH));
    }, []);

    const handleSubmit = React.useCallback(async () => {
        const trimmed = value.trim();

        if (trimmed.length !== CODE_LENGTH) {
            setLocalError(`The code is ${CODE_LENGTH} digits`);
            return;
        }

        setLocalError(null);

        try {
            await onSubmit(trimmed);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'An error occurred. Please try again.';
            setLocalError(message);
        }
    }, [onSubmit, value]);

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                handleSubmit();
            }
        },
        [handleSubmit]
    );

    const isSubmitDisabled = isLoading || value.trim().length !== CODE_LENGTH;
    const displayError = error ?? localError;

    return (
        <Box sx={styles.container}>
            <Box sx={styles.iconWrapper}>
                <VpnKeyIcon sx={styles.icon} />
            </Box>

            <Box sx={styles.headerSection}>
                <Typography variant="h6" sx={styles.title}>
                    Enter your code
                </Typography>
                <Typography variant="body2" sx={styles.subtitle}>
                    The 6-digit code from your authenticator app. It changes every 30 seconds.
                </Typography>
            </Box>

            {displayError && (
                <Alert severity="error" sx={styles.alert}>
                    {displayError}
                </Alert>
            )}

            <TextField
                fullWidth
                type="text"
                placeholder="123456"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                autoFocus
                slotProps={{
                    htmlInput: {
                        maxLength: CODE_LENGTH,
                        'aria-label': 'Authenticator code',
                        // A phone shows a number pad, and offers the code it just saw in a message
                        // or read from the authenticator.
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        autoComplete: 'one-time-code',
                    },
                }}
                sx={styles.challengeInput}
            />

            <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                sx={styles.submitButton}
            >
                {isLoading ? 'Verifying...' : 'Unlock secret'}
            </Button>
        </Box>
    );
};
